<template>
    <on-window-resize :execute="onWindowResize">
        <div>
            <div
                class="relative pb-24 overflow-hidden text-white xs:pb-32 md:pb-48 xl:pb-64 bg-blue"
                :style="{
                    '-webkit-clip-path': `polygon(0 0, 100% 0, 100% ${angle}%, 0 100%)`,
                    'clip-path': `polygon(0 0, 100% 0, 100% ${angle}%, 0 100%)`
                }"
            >
                <div class="container relative z-10 max-w-xl mx-auto">
                    <div class="max-w-lg py-8 mx-auto lg:mb-6 md:text-center">
                        <router-link
                            :to="{ name: 'www.home.index' }"
                            class="inline-block h-5 mb-5 md:mb-6 xl:mb-10"
                        >
                            <base-svg
                                name="logo-rentora"
                                class="h-full fill-primary-white"
                            />
                        </router-link>

                        <h1 class="mb-4 text-4xl font-semibold leading-tight tracking-tight font-noto-sans xs:text-5xl lg:text-6xl">
                            Rentora Inventories
                        </h1>

                        <h2 class="mb-8 text-lg sm:text-xl md:text-3xl lg:text-4xl md:mb-12">
                            The best in the business
                        </h2>

                        <laroute-link
                            :to="{
                                name: 'auth.users.create',
                            }"
                            class="inline-block w-full shadow shadow-md md:max-w-2xs btn btn-blue-lightest btn-xl"
                        >
                            Create an account
                        </laroute-link>

                        <callback-requests-create-button
                            class="block mx-auto mt-1"
                            role="landlord"
                        >
                            or, <strong>request a callback</strong>
                        </callback-requests-create-button>
                    </div>
                </div>

                <svg
                    class="absolute top-0 z-0"
                    :style="{
                        left: '60%',
                    }"
                    width="787"
                    height="847"
                    viewBox="0 0 787 847"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M103.281 230L202.781 -168H400.719L301.219 230H103.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M417.281 638L516.781 240H714.719L615.219 638H417.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M460 -369H660L560 31H360L460 -369Z"
                        fill="#3E8ED2"
                    />

                    <path
                        d="M618 -169H818L718 231H518L618 -169Z"
                        fill="#3E8ED2"
                    />

                    <path
                        d="M157.281 846L256.781 448H454.719L355.219 846H157.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M358 39H558L458 439H258L358 39Z"
                        fill="#3E8ED2"
                    />

                    <path
                        d="M1.28077 638L100.781 240H298.719L199.219 638H1.28077Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />
                </svg>

                <svg
                    class="absolute bottom-0 z-0"
                    :style="{
                        right: '60%',
                    }"
                    width="716"
                    height="488"
                    viewBox="0 0 716 488"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1.28077 599L100.781 201H298.719L199.219 599H1.28077Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M259.281 399L358.781 1H556.719L457.219 399H259.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M417.281 599L516.781 201H714.719L615.219 599H417.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M256 408H456L356 808H156L256 408Z"
                        fill="#3E8ED2"
                    />
                </svg>
            </div>

            <div class="container relative z-10 max-w-sm mx-auto -mt-24 lg:max-w-md xl:max-w-lg xs:-mt-32 md:-mt-48 xl:-mt-64">
                <img
                    src="/img/lp-inventories.png"
                    class="w-full"
                >
            </div>

            <div class="container max-w-2xl mx-auto">
                <grid-layout class="mt-6 mb-6 lg:mt-12 lg:mb-20">
                    <grid-layout-item class="mb-8 lg:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-collection"
                                class="w-8 h-8 fill-secondary-blue fill-primary-blue-lighter"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Unlimited
                        </h6>

                        <p>
                            A photo speaks a thousand words. That's why with Rentora you can store as many photos alongside your inventories as you like.
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 lg:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-camera"
                                class="w-8 h-8 fill-secondary-blue-lighter fill-primary-blue"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Quick and easy
                        </h6>

                        <p class="mb-3">
                            We built our inventory tool from the ground up to be fast and simple.
                        </p>

                        <p>
                            Take some photos and write a quick note. That's it.
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 lg:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-user-group"
                                class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Share with tenants
                        </h6>

                        <p class="mb-3">
                            Your inventories are shared with your tenants at the click of a button.
                        </p>

                        <p>
                            Make printed inventories a thing of the past.
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 md:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-cloud-upload"
                                class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Cloud-based
                        </h6>

                        <p>
                            Always available, you and your tenants have access to the inventories 24/7, on any device.
                        </p>
                    </grid-layout-item>
                </grid-layout>
            </div>

            <div
                class="py-16 bg-center bg-no-repeat lg:py-20 bg-blue-bg"
                id="payments"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 50px right 150px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-lg mx-auto">
                    <div class="mx-auto mb-8 text-center lg:w-2/3">
                        <h2 class="mb-4 text-2xl font-semibold leading-tight tracking-tight font-noto-sans text-grey-darkest xs:text-3xl md:text-4xl lg:text-5xl">
                            Get started
                        </h2>
                    </div>

                    <div class="text-center">
                        <laroute-link
                            :to="{
                                name: 'auth.users.create',
                            }"
                            class="w-full shadow shadow-md md:max-w-2xs btn btn-blue btn-xl"
                        >
                            Create an account
                        </laroute-link>

                        <callback-requests-create-button
                            class="block mx-auto mt-1"
                            role="landlord"
                        >
                            or, <strong>request a callback</strong>
                        </callback-requests-create-button>
                    </div>
                </div>
            </div>

            <div class="container max-w-xl mx-auto">
                <div class="container max-w-xl pb-6 mx-auto mt-6">
                    <div class="flex flex-col items-center justify-center text-xs">
                        <div class="flex-none px-3">
                            <router-link
                                :to="{ name: 'www.policies.terms.index' }"
                                class="underline"
                            >
                                Terms,
                            </router-link>

                            <router-link
                                :to="{ name: 'www.policies.privacy.index' }"
                                class="underline"
                            >
                                Privacy
                            </router-link>

                            &amp;

                            <router-link
                                :to="{ name: 'www.policies.cookies.index' }"
                                class="underline"
                            >
                                Cookie
                            </router-link>

                            policies
                        </div>

                        <div class="flex-none px-3">
                            &copy; {{ $moment().format('Y') }} Tenant Manager Ltd.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </on-window-resize>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../../mixins/head'

    export default {
        mixins: [
            HeadMixin
        ],
        data () {
            return {
                angle: 100
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'All-in-one property management',
                        'Rent collection, listings, inventories, and more'
                    ],
                    description: 'Collect rent through Rentora and have the money go straight into your bank account - no worries, no hassle.',
                    meta: [
                        {
                            property: 'og:image',
                            content: _.url('/img/head/og-pay-rent-with-credit-card.png')
                        },
                        {
                            name: 'twitter:image',
                            content: _.url('/img/head/twitter-pay-rent-with-credit-card.png')
                        }
                    ]
                }
            },
            onWindowResize () {
                switch (true) {
                    case this.$screen.is('lg'):
                        this.angle = 85
                        break
                    case this.$screen.is('md'):
                        this.angle = 90
                        break
                    case this.$screen.is('xs'):
                        this.angle = 94
                        break
                    default:
                        this.angle = 96
                }
            }
        }
    }
</script>
